import { useCallback, useMemo, useState } from 'react';

import { generatePath, LoaderFunctionArgs, useNavigate, useParams } from 'react-router-dom';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { Button, Flex, SimpleGrid, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import {
  Badge,
  Campaign,
  campaignsService,
  CampaignWinner,
  FetchCampaignsResponse,
  getError,
  useToast,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import { Table } from 'app/components/UI/Table/Table';
import useFetchCampaigns from 'app/hooks/useFetchCampaigns';
import useFetchCampaignAnnouncements from 'app/hooks/useFetchCampaignAnnouncements';
import useRetryCampaign from 'app/hooks/useRetryCampaign';
import usePublishCampaign from 'app/hooks/usePublishCampaign';
import useUnpublishCampaign from 'app/hooks/useUnpublishCampaign';
import useGroups from 'app/hooks/useGroups';
import usePots from 'app/hooks/usePots';
import TopUpPotModal from 'app/components/Modals/TopUpPotModal/TopUpPotModal';
import TransferPointsModal from 'app/components/Modals/TransferPointsModal/TransferPointsModal';
import { companyPaths } from 'app/constants/routes';
import { useManagingCompanyStore } from 'app/store';
import { campaignStatusDetails, describeReach, formatDateTime } from 'app/utils/table/campaigns';
import { describeRecurrence } from 'app/utils/campaigns/recurrence';
import { stateMoveProblem } from 'app/utils/campaigns/stateMove';
import getPrideColumns, { PrideRow } from 'app/utils/table/campaigns/prides';
import { getPointsValue } from 'app/utils/points';
import { formatCurrency } from 'app/utils/strings';

import CancelCampaignModal from '../CancelCampaignModal';

import CampaignAnnouncements from './CampaignAnnouncements';
import CampaignSummary from './CampaignSummary';
import CampaignValueCards from './CampaignValueCards';
import DeleteCampaignModal from './DeleteCampaignModal';
import EditCampaignModal from './EditCampaignModal';
import ManagePrideManagersModal from './ManagePrideManagersModal';
import useManagerDirectory from './useManagerDirectory';

const winnerName = (winner: CampaignWinner) => `${winner.firstName} ${winner.lastName}`.trim();

/** The prides a campaign runs in. A campaign with one company-wide winner is its own single row. */
/**
 * The prides in the order they sit under one another, each told how deep it is.
 *
 * A campaign's prides are returned flat, so a company running one across a region and its sites reads as a
 * list of equals. Ordered here so a pride follows whatever contains it, and siblings read alphabetically.
 */
function inHierarchyOrder(rows: PrideRow[], parentOf: Map<string, string | null>): PrideRow[] {
  const present = new Set(rows.map((row) => row.groupId).filter(Boolean) as string[]);
  const byParent = new Map<string | null, PrideRow[]>();

  rows.forEach((row) => {
    // A pride whose parent is not itself in this campaign hangs from the top, not from nothing.
    const parent = row.groupId ? parentOf.get(row.groupId) ?? null : null;
    const key = parent && present.has(parent) ? parent : null;
    byParent.set(key, [...(byParent.get(key) ?? []), row]);
  });

  const ordered: PrideRow[] = [];

  const walk = (key: string | null, depth: number) => {
    [...(byParent.get(key) ?? [])]
      .sort((a, b) => a.prideName.localeCompare(b.prideName))
      .forEach((row) => {
        ordered.push({ ...row, depth });
        if (row.groupId) walk(row.groupId, depth + 1);
      });
  };

  walk(null, 0);

  return ordered;
}

function prideRowsOf(campaign: Campaign, prideNames: Map<string, string>): PrideRow[] {
  // A campaign with one company-wide winner fans out to nothing, so it is its own single row.
  const children = campaign.children?.length ? campaign.children : [campaign];

  return children.map((child) => {
    const winner = child.winners?.[0];

    return {
      id: child.id,
      groupId: child.groupId,
      prideName: child.groupId ? prideNames.get(child.groupId) ?? 'Unknown pride' : 'Whole company',
      status: child.status,
      outcome: child.outcome,
      winner: winner ? winnerName(winner) : '-',
      managerIds: child.campaignManagerIds ?? [],
      attentionReasons: child.attentionReasons ?? [],
      depth: 0,
      retryable: child.retryable,
      needsCampaignManager: child.needsCampaignManager,
      nominationCount: child.nominationCount ?? 0,
    };
  });
}

function noFundingMessage(campaign: Campaign) {
  if (campaign.awardPoints === null || campaign.awardPoints === undefined) {
    return 'This campaign has no award, so it has no funding pot. Set an award and its pot is created with it.';
  }

  return 'This campaign has no funding pot. No winner can be chosen until one exists.';
}

function reservedPoints(funding: NonNullable<Campaign['funding']>) {
  return Math.max(funding.balance - funding.available, 0);
}

function Detail({
  label,
  value,
  hint = undefined,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  const heading = hint ? (
    <Tooltip label={hint} hasArrow>
      <Text
        textStyle="extraSmallRegular"
        color="brand.darkGray"
        aria-label={`${label}: ${hint}`}
        w="fit-content"
      >
        {label}
      </Text>
    </Tooltip>
  ) : (
    <Text textStyle="extraSmallRegular" color="brand.darkGray">
      {label}
    </Text>
  );

  return (
    <Flex flexDir="column" gap="2px">
      {heading}
      <Text textStyle="smallMedium" color="brand.black">
        {value}
      </Text>
    </Flex>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Flex
      bgColor="brand.white"
      borderRadius="10px"
      border="1px solid"
      borderColor="brand.lightGray1"
      w="full"
      p="20px"
      flexDir="column"
      gap="16px"
    >
      <Text as="h5" textStyle="h5SemiBold" color="brand.darkPurple">
        {title}
      </Text>
      {children}
    </Flex>
  );
}

const ELIGIBILITY_LABELS: Record<string, string> = {
  ALL_COMPANY: 'Anyone in the company',
  ALL_GROUP: 'Only people in that pride',
  SELECTED_GROUPS: 'Chosen prides',
  SELECTED: 'A chosen shortlist',
};

/** A pride that has finished is left alone by a cancel, so only these are what cancelling would stop. */
const RUNNING: Campaign['status'][] = [
  'PENDING',
  'VOTING_OPEN',
  'VOTING_CLOSED',
  'INTERNAL_REVIEW_OPEN',
  'INTERNAL_REVIEW_CLOSED',
];

/**
 * The campaign a breadcrumb names, read before the page renders.
 * <p>
 * Asked for under the key the page itself uses, so landing here costs one read rather than two.
 */
export const campaignDetailLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const request = {
      companyId: params.companyId as string,
      id: params.campaignId,
      includeFunding: true,
      page: 0,
      size: 1,
    };

    const page =
      queryClient.getQueryData<FetchCampaignsResponse>(['fetchCampaigns', request]) ??
      (await queryClient.fetchQuery({
        queryKey: ['fetchCampaigns', request],
        queryFn: () => campaignsService.fetchCampaigns(request),
      }));

    return page?.list?.[0] ?? null;
  };

function CampaignDetail() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const edit = useDisclosure();
  const cancel = useDisclosure();
  const remove = useDisclosure();
  const topUp = useDisclosure();
  const moveOut = useDisclosure();
  const [managing, setManaging] = useState<PrideRow>();
  const { campaignId } = useParams();
  const company = useManagingCompanyStore((state) => state.company);
  const companyId = company?.id || '';

  const { data: campaignsPage, isInitialLoading } = useFetchCampaigns({
    companyId,
    id: campaignId,
    includeFunding: true,
    page: 0,
    size: 1,
  });
  const campaign = campaignsPage?.list?.[0];

  // The campaign's own funding pot, read whole: the campaign carries its balance, but topping it up or moving
  // points out is done to the pot itself and needs its name and its id.
  const potId = campaign?.funding?.potId;
  const { data: potsPage } = usePots.useFetchPots(
    { companyIds: [companyId], ids: potId ? [potId] : [], page: 0, size: 1 },
    !!companyId && !!potId
  );
  const fundingPot = potsPage?.list?.[0];

  const { data: groupsPage } = useGroups.useFetchGroups(
    { companyId, page: 0, size: 9999 },
    !!companyId
  );
  const prideNames = useMemo(
    () => new Map((groupsPage?.list ?? []).map((group) => [group.id, group.name])),
    [groupsPage]
  );

  const parentOf = useMemo(
    () =>
      new Map<string, string | null>(
        (groupsPage?.list ?? []).map((group) => [group.id, group.parentGroupId ?? null])
      ),
    [groupsPage]
  );

  const prides = useMemo(
    () => (campaign ? inHierarchyOrder(prideRowsOf(campaign, prideNames), parentOf) : []),
    [campaign, prideNames, parentOf]
  );

  const appointedIds = useMemo(
    () => [...new Set(prides.flatMap((pride) => pride.managerIds))],
    [prides]
  );
  const { memberships, nameOf } = useManagerDirectory(companyId, appointedIds);

  const { data: announcements, isInitialLoading: isLoadingAnnouncements } =
    useFetchCampaignAnnouncements(campaignId);

  const { mutate: publish, isLoading: isPublishing } = usePublishCampaign({
    onSuccess: (published) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign published',
        description: `${published.name} will open for nominations on schedule.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not publish the campaign',
        description: stateMoveProblem(error),
      }),
  });

  const { mutate: unpublish, isLoading: isUnpublishing } = useUnpublishCampaign({
    onSuccess: (withdrawn) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign back to draft',
        description: `${withdrawn.name} will not open until it is published again.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not put the campaign back to a draft',
        description: stateMoveProblem(error),
      }),
  });

  const { mutate: retry } = useRetryCampaign({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Announcing again',
        description: 'The announcement carries on from where it stopped.',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not announce again',
        description: getError(error).message || 'Nothing was changed. Please try again.',
      }),
  });

  const openPride = useCallback(
    (pride: PrideRow) =>
      navigate(
        generatePath(companyPaths.campaignPrideDetail.path, {
          companyId,
          campaignId: campaignId as string,
          prideCampaignId: pride.id,
        })
      ),
    [companyId, campaignId, navigate]
  );

  const onRetry = useCallback((pride: PrideRow) => retry(pride.id), [retry]);

  const columns = useMemo(
    () => getPrideColumns(openPride, setManaging, onRetry, nameOf),
    [openPride, onRetry, nameOf]
  );

  if (isInitialLoading) {
    return (
      <AuthenticatedPageWrapper title="Campaign">
        <CenterSpinner />
      </AuthenticatedPageWrapper>
    );
  }

  if (!campaign) {
    return (
      <AuthenticatedPageWrapper title="Campaign">
        <Text color="brand.darkPurple">This campaign could not be found.</Text>
      </AuthenticatedPageWrapper>
    );
  }

  const status = campaignStatusDetails[campaign.status];
  const runningPrides = prides.filter((pride) => RUNNING.includes(pride.status)).length;
  const award =
    campaign.awardPoints === null || campaign.awardPoints === undefined
      ? 'No points'
      : `${campaign.awardPoints.toLocaleString('en-GB')} pts · ${formatCurrency(
          getPointsValue(campaign.awardPoints),
          'GBP'
        )}`;

  return (
    <AuthenticatedPageWrapper title={campaign.name}>
      <Flex flexDir="column" gap="24px">
        <Flex align="flex-start" justify="space-between" gap="15px">
          <Flex flexDir="column" gap="8px">
            <Flex align="center" gap="10px">
              <Text as="h2" textStyle="h2" color="brand.darkPurple">
                {campaign.name}
              </Text>
              <Badge value={status.label} status={status.badgeStatus} />
              {campaign.recurring && <Badge value="Recurring" status="created" />}
            </Flex>
            {campaign.description && <Text color="brand.darkPurple">{campaign.description}</Text>}
            {campaign.statusReason && (
              <Text textStyle="extraSmallRegular" color="text.error">
                {campaign.statusReason}
              </Text>
            )}
          </Flex>
          <Flex gap="12px">
            {campaign.status === 'DRAFT' && (
              <Button
                colorScheme="purple"
                isLoading={isPublishing}
                onClick={() => publish(campaign.id)}
                data-testid="publish-campaign"
              >
                Publish
              </Button>
            )}
            {campaign.status === 'PENDING' && (
              <Button
                variant="outline"
                isLoading={isUnpublishing}
                onClick={() => unpublish(campaign.id)}
                data-testid="unpublish-campaign"
              >
                Put back to draft
              </Button>
            )}
            <Button variant="outline" onClick={edit.onOpen} data-testid="open-edit-campaign">
              Edit
            </Button>
            {runningPrides > 0 && campaign.status !== 'PENDING' && (
              <Button
                variant="outline"
                colorScheme="red"
                onClick={cancel.onOpen}
                data-testid="open-cancel-campaign"
              >
                Cancel campaign
              </Button>
            )}
            {(campaign.status === 'PENDING' || campaign.status === 'DRAFT') && (
              <Button
                variant="outline"
                colorScheme="red"
                onClick={remove.onOpen}
                data-testid="open-delete-campaign"
              >
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(generatePath(companyPaths.campaigns.path, { companyId }))}
              data-testid="back-to-campaigns"
            >
              Back to campaigns
            </Button>
          </Flex>
        </Flex>

        <CampaignSummary prides={prides} />

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px">
          <Card title="How it runs">
            <SimpleGrid columns={2} gap="16px">
              <Detail label="Where it runs" value={describeReach(campaign)} />
              <Detail label="Award" value={award} />
              <Detail
                label="Who may nominate"
                value={
                  ELIGIBILITY_LABELS[campaign.nominatorEligibility] ?? campaign.nominatorEligibility
                }
              />
              <Detail
                label="Who may be nominated"
                value={
                  ELIGIBILITY_LABELS[campaign.nomineeEligibility] ?? campaign.nomineeEligibility
                }
              />
              <Detail
                label="Nominations per person"
                value={`${campaign.nominationsPerPerson ?? 1}`}
              />
              <Detail label="A value is required" value={campaign.valuesRequired ? 'Yes' : 'No'} />
              <Detail
                label="Winner may have no nominations"
                value={campaign.allowWinnerWithoutNominations ? 'Yes' : 'No'}
              />
              {campaign.allowWinnerWithoutNominations && (
                <Detail
                  label="Who may win"
                  value={
                    ELIGIBILITY_LABELS[campaign.winnerEligibility ?? 'ALL_COMPANY'] ??
                    campaign.winnerEligibility
                  }
                />
              )}
              <Detail label="Timezone" value={campaign.timezone ?? '-'} />
              <Detail
                label="Repeats"
                value={describeRecurrence(campaign.recurring, campaign.recurrenceRule)}
              />
            </SimpleGrid>
          </Card>

          <Card title="Schedule">
            <SimpleGrid columns={2} gap="16px">
              <Detail label="Nominations open" value={formatDateTime(campaign.nominationsOpenAt)} />
              <Detail
                label="Nominations close"
                value={formatDateTime(campaign.nominationsCloseAt)}
              />
              <Detail label="Review opens" value={formatDateTime(campaign.reviewOpensAt)} />
              <Detail label="Review closes" value={formatDateTime(campaign.reviewClosesAt)} />
              <Detail label="Winner announced" value={formatDateTime(campaign.announcementAt)} />
            </SimpleGrid>
          </Card>
        </SimpleGrid>

        <Card title="Funding">
          {campaign.funding ? (
            <SimpleGrid columns={{ base: 2, lg: 5 }} gap="16px">
              <Detail
                label="Pot balance"
                value={`${campaign.funding.balance.toLocaleString('en-GB')} pts`}
              />
              <Detail
                label="Reserved"
                hint="Awards held for winners already selected, waiting to be announced"
                value={`${reservedPoints(campaign.funding).toLocaleString('en-GB')} pts`}
              />
              <Detail
                label="Available"
                value={`${campaign.funding.available.toLocaleString('en-GB')} pts`}
              />
              <Detail
                label="Still to pay"
                hint="Awards for the prides still to be decided. A pride already decided is held instead."
                value={`${(campaign.committedPoints ?? 0).toLocaleString('en-GB')} pts`}
              />
              <Flex flexDir="column" gap="2px">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Covers what is left
                </Text>
                <Flex>
                  {campaign.funding.coversCommitment ? (
                    <Badge value="Yes" status="active" />
                  ) : (
                    <Badge value="Short" status="left" />
                  )}
                </Flex>
              </Flex>
            </SimpleGrid>
          ) : (
            <Text textStyle="smallRegular" color="brand.darkGray">
              {noFundingMessage(campaign)}
            </Text>
          )}

          {fundingPot && (
            <Flex gap="12px" pt="16px">
              <Button variant="outline" onClick={topUp.onOpen} data-testid="open-top-up-pot">
                Top up
              </Button>
              <Button
                variant="outline"
                isDisabled={fundingPot.balance <= 0}
                onClick={moveOut.onOpen}
                data-testid="open-move-points-out"
              >
                Move points out
              </Button>
            </Flex>
          )}
        </Card>

        <Card title="Winners announced">
          <CampaignAnnouncements
            announcements={announcements?.list ?? []}
            isLoading={isLoadingAnnouncements}
          />
        </Card>

        <Card title="Cards">
          <CampaignValueCards campaign={campaign} companyId={companyId} prideNames={prideNames} />
        </Card>

        <Flex flexDir="column" gap="10px">
          <Text as="h5" textStyle="h5SemiBold" color="brand.darkPurple">
            Prides
          </Text>
          <Flex flexDir="column" data-testid="prides-table">
            <Table
              columns={columns}
              data={prides}
              getRowId={(pride) => pride.id}
              pageNumber={0}
              pageSize={prides.length || 1}
              total={prides.length}
              sorting={[]}
              onPaginationChange={() => {}}
              onSortingChange={() => {}}
            />
          </Flex>
        </Flex>
      </Flex>

      {managing && (
        <ManagePrideManagersModal
          isOpen
          onClose={() => setManaging(undefined)}
          pride={prides.find((pride) => pride.id === managing.id) ?? managing}
          prides={prides}
          companyId={companyId}
          memberships={memberships}
          nameOf={nameOf}
        />
      )}

      {/* Mounted only while open, so closing it drops what was typed rather than keeping it to be
          mistaken for a saved change the next time it opens. */}
      {edit.isOpen && <EditCampaignModal isOpen onClose={edit.onClose} campaign={campaign} />}
      <DeleteCampaignModal
        isOpen={remove.isOpen}
        onClose={remove.onClose}
        campaign={campaign}
        companyId={companyId}
      />
      <CancelCampaignModal
        isOpen={cancel.isOpen}
        onClose={cancel.onClose}
        campaignId={campaign.id}
        title="Cancel this campaign?"
        summary={
          runningPrides === 1
            ? 'The one pride still running stops.'
            : `All ${runningPrides} prides still running stop. A pride that has already finished is left as it is.`
        }
        successDescription={`${campaign.name} has stopped. Its nominations and any decision have been kept.`}
      />
      <TopUpPotModal isOpen={topUp.isOpen} onClose={topUp.onClose} originPot={fundingPot} />
      <TransferPointsModal
        isOpen={moveOut.isOpen}
        onClose={moveOut.onClose}
        originPot={fundingPot}
      />
    </AuthenticatedPageWrapper>
  );
}

export default CampaignDetail;
